import React, { useLayoutEffect, useRef } from "react";

// Stroke-drawn plates for the work index — one emblem per project, in the same
// language as the aperture mark: thin strokes on lifted ink that draw
// themselves in on hover. These stand in until a project has real imagery;
// swap one out by pointing the project's `art` key in site.js elsewhere.
//
// Each stroke's true length is measured once on mount and exposed as --len,
// which the CSS uses for the dash-draw animation — the same measured-length
// approach as the intro mark. (The pathLength="1" shortcut is deliberately
// avoided: Chromium misrenders dasharray+pathLength on circles, leaving a
// visible arc gap.) `--i` staggers the strokes; fractional values keep long
// sequences tight.
const stroke = (i, accent) => ({
  className: accent ? "dp-art__stroke dp-art__stroke--accent" : "dp-art__stroke",
  style: { "--i": i },
  fill: "none",
});

// Two people, one rhythm: a pair of figures whose paths weave like a plait.
function Tango() {
  return (
    <>
      <circle {...stroke(0)} cx="96" cy="46" r="7" />
      <circle {...stroke(0.5, true)} cx="144" cy="46" r="7" />
      <path
        {...stroke(1)}
        d="M96 64 C96 108 144 122 144 164 C144 206 96 220 96 254"
      />
      <path
        {...stroke(1.5, true)}
        d="M144 64 C144 108 96 122 96 164 C96 206 144 220 144 254"
      />
      {/* Straight strokes are <path>s, not <line>s: Chromium's dashed-stroke
          painting glitches when a dashed <line> shares a plate with dashed
          circles (the dial's centre ring lost an arc). */}
      <path {...stroke(2.5)} d="M 70 266 L 170 266" opacity="0.3" />
    </>
  );
}

// A gentle companion: a small presence, arcs radiating softly outward.
function Lucy() {
  return (
    <>
      <circle {...stroke(0, true)} cx="120" cy="205" r="10" />
      <path {...stroke(1)} d="M85.4 185 A40 40 0 0 1 154.6 185" opacity="0.85" />
      <path {...stroke(1.7)} d="M59.4 170 A70 70 0 0 1 180.6 170" opacity="0.5" />
      <path {...stroke(2.4)} d="M33.4 155 A100 100 0 0 1 206.6 155" opacity="0.25" />
    </>
  );
}

// Pick up the phone and hear another time: a rotary dial with its finger stop.
function Calls() {
  const holes = [
    [147.5, 104.4],
    [120, 97],
    [92.5, 104.4],
    [72.4, 124.5],
    [65, 152],
    [72.4, 179.5],
    [92.5, 199.6],
    [120, 207],
    [147.5, 199.6],
  ];
  return (
    <>
      <circle {...stroke(0)} cx="120" cy="152" r="84" />
      <circle {...stroke(1, true)} cx="120" cy="152" r="24" />
      {holes.map(([cx, cy], n) => (
        <circle
          key={cx + "," + cy}
          {...stroke(2 + n * 0.4)}
          cx={cx}
          cy={cy}
          r="8.5"
          opacity="0.7"
        />
      ))}
      <path {...stroke(5.8, true)} d="M 179.9 168.1 L 195.3 172.2" />
    </>
  );
}

const ART = { tango: Tango, lucy: Lucy, calls: Calls };

export default function ProjectArt({ id }) {
  const ref = useRef(null);
  // Until --len is set, the dasharray calc() is invalid and the strokes render
  // fully drawn — so a measurement failure degrades to a static emblem.
  useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.querySelectorAll(".dp-art__stroke").forEach((el) => {
      el.style.setProperty("--len", el.getTotalLength().toFixed(1));
    });
  }, [id]);

  const Art = ART[id];
  if (!Art) return null;
  return (
    <svg ref={ref} viewBox="0 0 240 300" aria-hidden="true" focusable="false">
      <Art />
    </svg>
  );
}
